import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from "../../../api/Base";
import { showError } from "../../../utils/showAlert";
import HostApi from '../../../api/host/HostApi';

// Third-party packages.
import * as dayjs from 'dayjs';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';

import { CSVLink } from 'react-csv';

import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Context.
import { EventTypeContext } from '../../../context/EventTypeContext';
import { HostContext } from "../../../context/HostContext";

// Types.
import HostsAdminDataTable from '../../../types/host/tables/HostsAdminDataTable';
import HostTarget from "../../../types/host/HostTarget";

// Constants.
import { DATA_LOADING } from '../../../component/Constants';

// Helpers
import { getDefaultPageSize } from "../../../helpers/utils";
import SearchInput from '../../../component/SearchInput';

// Components.
import StatusIcon from '../../../component/StatusIcon';
import HostType from '../../../component/host/HostType';
import ActionButtons from '../../../component/buttons/ActionButtons';
import NoData from '../../../component/NoData';
import TablePagination from '../../../component/TablePagination';
import SortClass from "../../../component/SortClass";
import ConditionalRender from "../../../helpers/ConditionalRender";

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(relativeTime.default);
dayjs.extend(customParseFormat.default);

function List(props: { loading: number, hostsAdminDataTable: HostsAdminDataTable[], isAdmin: boolean }) {
    const { eventTypes } = useContext(EventTypeContext);
    const { hosts, setHosts } = useContext(HostContext);
    const [agents, setAgents] = useState<any[]>([]);
    const [hostsList, setHostsList] = useState<HostsAdminDataTable[]>([]);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());


    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const removeHost = (hostId: number) => {
        const updatedHostsList = hostsList.filter(host => host.id !== hostId);
        setHostsList(updatedHostsList);
    }



    const duplicateHost = async (host: any) => {
        // Generate unique name with "copy" suffix
        const baseName = host.host.name;
        const existingNames = hosts.map(hst => hst.name);
        let newName = `${baseName} copy`;
        let counter = 2;

        while (existingNames.includes(newName)) {
            newName = `${baseName} copy ${counter}`;
            counter++;
        }

        // Create duplicate data based on original host
        const duplicateData = [{
            name: newName,
            host: host.host.host,
            agentid: host.host.agentid,
            type: host.host.type,
            monitor: false, // Start stopped
            debug: host.host.debug,
            port: host.host.port,
            username: host.host.username,
            password: host.host.password,
            passwordchanged: false,
            keyfile: host.host.keyfile,
            region: host.host.region,
            monitoringintervalmilliseconds: host.host.monitoringintervalmilliseconds,
            encrypted: host.host.encrypted
        }];

        try {
            const result = await api.post('host', duplicateData);
            if (result.status === 200) {
                // Refresh hosts list
                const updatedHosts = await HostApi.getHosts();
                setHosts(updatedHosts);
            }
        } catch (error: any) {
            console.error('Failed to duplicate host:', error);
            showError('Failed to duplicate host', error.message || error.toString());
        }
    }

    const data = useMemo(() => hostsList.filter(row => (row.name.toLowerCase().includes(tableFilter.toLowerCase())) || (row.hostAddress.toLowerCase().includes(tableFilter.toLowerCase()))), [hostsList, tableFilter]);

    useEffect(() => {
        setHostsList(props.hostsAdminDataTable)
    }, [props.hostsAdminDataTable]);

    useEffect(() => {

        const getAgents = async() => {
            await api.get(`agent `)
                .then((response) => {
                    if (response) {
                        setAgents(response.data);
                    }
                })
                .catch((error: any) => {
                    console.error('Failed to retrieve agents.', error);
                    showError('Failed to retrieve agents', error.message || error.toString());
                });
        }
        void getAgents();
    }, []);

    const isHostDeleted = (item: any) => {
        const reason = item?.host?.reason || item.reason
        return reason === 'Deletion in progress';
    }

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            accessor: 'host',
            disableSortBy: true
        },
        {
            Header: 'Host',
            accessor: 'name',
            className: 'fw-bold',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                <React.Fragment>
                    <StatusIcon hostId={cell.row.values.id} removeFromList={removeHost} mode="admin" />
                    {cell.row.values.name}
                </React.Fragment>
            )
        },
        {
            Header: '',
            accessor: 'type',
            className: 'text-center col-width-5',
            headerClassName: 'text-center col-width-5',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                <React.Fragment>
                    <HostType host={cell.row.values.host} />
                </React.Fragment>
            )
        },
        {
            Header: 'Host Address',
            accessor: 'hostAddress',
            className: 'concatenate',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                cell.row.values.hostAddress
            )
        },
        {
            Header: 'Agent',
            accessor: 'agent',
            sortType: (rowA: any, rowB: any) => {
                return (rowA.values.host.agentid - rowB.values.host.agentid)
            },
            Cell: ({cell}) => {
                const getAgent = agents.find(agent => agent.id === cell.row.values.host.agentid)
                return getAgent ? getAgent.name : ''
            }
        },
        {
            Header: 'Agent Version',
            accessor: 'version',
            sortType: (rowA: any, rowB: any) => {
                return (rowA.values.host.agentid - rowB.values.host.agentid)
            },
            Cell: ({cell}) => {
                const getAgent = agents.find(agent => agent.id === cell.row.values.host.agentid)
                return getAgent?.version ? getAgent.version : ''
            }
        },
        {
            Header: <div className='d-flex justify-content-between'><span className='w-25 text-start'>Last Contacted</span><span>Actions</span></div>,
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }) => {
                const isDeleted = isHostDeleted(cell.row.original)
                return isDeleted ? 'Deletion in progress' :  <ActionButtons isAdmin={props.isAdmin} host={cell.row.values.host} eventTypes={eventTypes} onDuplicate={duplicateHost} rowData={cell.row.original}/>
            }
        }
    ], [eventTypes, agents, tableFilter]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
        columns,
        data,
        initialState: { hiddenColumns: ['id', 'host'],
            pageIndex: initialPageIndex,
            pageSize: initialPageSize, sortBy: [{ id: 'name', desc: false }] }
    },
        useSortBy, usePagination);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    const startStopAll = async (isStart: boolean) => {
        const changedIds: number[] = [];

        const updatedHosts = hosts.map((obj: HostTarget) => {
            const updatedObj = {
                ...obj,
                monitor: isStart,
                password: obj.password === '' ? null : obj.password,
            };
            const isDeleted = isHostDeleted(updatedObj)

            if (updatedObj.monitor !== obj.monitor && !isDeleted) {
                changedIds.push(updatedObj.id);
            }

            return updatedObj;
        });
        const selectedHosts = updatedHosts.filter(host =>
            changedIds.includes(host.id)
        );
        try {
            if (changedIds.length) {
                await api.put('host', selectedHosts);
                setHosts(updatedHosts);
            }
        } catch (error: any) {
            console.error('Failed to update monitor state on backend:', error);
            showError('Failed to update monitor state on backend', error.message || error.toString());
        }
    };

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-server fa-fw" aria-hidden="true"></i>
                    Monitored hosts
                    <span className="badge bg-info" data-tip="Total host count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseHosts" aria-expanded="false" aria-controls="collapseHosts"></i>
                </div>
                <div id="collapseHosts" className="card-body collapse show">
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-10">
                            <ConditionalRender if={props.isAdmin}>
                                <button type="button" className='btn btn-sm btn-outline-secondary' data-tip='Start all'
                                        onClick={() => startStopAll(true)}>
                                    <i className='fal fa-play fa-fw'></i><span>Start all</span>
                                </button>
                                <button type="button" className='btn btn-sm btn-secondary' data-tip='Stop all'
                                        onClick={() => startStopAll(false)}>
                                    <i className='fal fa-stop fa-fw'></i><span>Stop all</span>
                                </button>
                            </ConditionalRender>
                            <Link to="/admin/hosts/create" role="button" className="btn btn-sm btn-success">
                                <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                            </Link>
                            <CSVLink role="button"
                                     headers={[{ label: 'ID', key: 'id' }, { label: 'Host Name', key: 'name' }, { label: 'Type', key: 'type' }, {
                                         label: 'Host Address',
                                         key: 'hostAddress'
                                     }, { label: 'Last Contact', key: 'updated' }]}
                                     data={data}
                                     download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Hosts.csv`}
                                     className="btn btn-sm btn-primary">
                                <i className="fal fa-file-export fa-fw"></i><span>Export</span>
                            </CSVLink>
                        </div>
                        <div className="col col-md-2">
                            <SearchInput
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table {...getTableProps()} className="table">
                            <thead>
                            {headerGroups.map(headerGroup => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map(column => (
                                            <th {...column.getHeaderProps(column.getSortByToggleProps())} className={(column as any).className}>
                                                {column.render('Header')}
                                                <SortClass column={column} />
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                            {page.map(row => {
                                prepareRow(row)
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell) => {
                                            return (
                                                <td {...cell.getCellProps ()} className={(cell.column as any).className}>
                                                    {cell.render('Cell')}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                            </tbody>
                        </table>
						<NoData
							error={null}
							loading={props.loading === DATA_LOADING}
							length={data.length}
						/>
						<TablePagination
							length={data.length}
							pageSize={pageSize}
							setPageSize={setPageSize}
							pageOptions={pageOptions}
							pageCount={pageCount}
							canPreviousPage={canPreviousPage}
							previousPage={previousPage}
							canNextPage={canNextPage}
							nextPage={nextPage}
							gotoPage={gotoPage}
							pageIndex={pageIndex}
						/>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default List;
